// What Bailey reads to do what was asked: the salon's own reviews, team,
// menu, vouchers and diary. Each is read only when a request needs it, kept
// for a few minutes, and never waited on for more than a few seconds (Bailey
// says what's missing instead).
import { useContext, useMemo } from 'react'
import { ReactReduxContext } from 'react-redux'
import { newServicesAction, hairdresserActions } from '../../../../../_actions'
import { giftCardService } from '../../../../../_services/giftCard.service'
import { freeWeek, myStaffId } from './freeSlots'
import { minutesOf } from './dates'
import { loadReviews } from './reviews'

const MINUTES = 60 * 1000
const cache = new Map()
const salonKey = () => { try { return localStorage.getItem('salonId') || '' } catch (e) { return '' } }
const once = (key, load, ttl = 5 * MINUTES) => {
  const k = `${salonKey()}:${key}`
  const hit = cache.get(k)
  if (hit && Date.now() - hit.at < ttl) return hit.job
  const job = Promise.resolve().then(load).catch(() => null)
  cache.set(k, { at: Date.now(), job })
  // A failed read isn't kept, so the next request tries again
  job.then((v) => { if (v == null) cache.delete(k) })
  return job
}
const within = (job, ms) => Promise.race([job, new Promise(resolve => setTimeout(resolve, ms))])
export const forgetAskData = () => cache.clear()

// The salon's services with prices, as the menu has them
const loadServices = async (dispatch) => {
  const response = await dispatch(newServicesAction.listServicesData({}))
  const out = []
  ;((response && response.listServices) || []).forEach((category) => {
    (category.serviceList || []).forEach((s) => {
      if (s && !s.isDeactivated && s.serviceName) {
        out.push({ id: s.commonServiceId, name: s.serviceName, price: Number(s.servicePrice) || 0, duration: s.serviceTime ? `${s.serviceTime} min` : '', category: category.categoryName || '' })
      }
    })
  })
  return out
}
// The team, with a job title and photo when they have one
const loadTeam = async (dispatch) => {
  const response = await dispatch(hairdresserActions.hairdresserStaffRota({
    searchKey: '', hairdresserId: localStorage.getItem('userId'), page: 1, listType: 1, isGetRota: 1,
  }))
  const rota = (response && response.data && response.data.data && response.data.data.rota) || []
  return rota.filter(h => h && h.name && h.name !== 'Unknown').map((h) => {
    const title = h.hairdresserInfo && h.hairdresserInfo.jobTitle
    return { id: h._id, name: h.name, role: title && title !== 'Stylist' ? title : '', image: (h.userImage && h.userImage.circleImage) || null }
  })
}
// Gift vouchers on sale online, with their lowest price
export const loadVouchers = async () => {
  const list = await giftCardService.listVouchers()
  return (list || []).filter(v => v && v.status === 'active' && v.online).map(v => ({
    id: v.id,
    name: v.name || '',
    fromPrice: Array.isArray(v.denominations) && v.denominations.length ? Math.min(...v.denominations.map(Number).filter(Boolean)) : Number(v.value) || 0,
  }))
}

// Which of the salon's records a request needs
export const dataFor = (req, post) => {
  const type = (req && req.job) || (post && post.type) || null
  const f = (req && req.fields) || {}
  return {
    reviews: type === 'review',
    team: type === 'team' || !!f.byRaw,
    services: type === 'offer' || type === 'priceList' || type === 'oneSlot',
    vouchers: type === 'giftVouchers',
    free: type === 'freeSlots' || type === 'oneSlot',
  }
}

// The day a free slots post is about: the one asked for, or the first with gaps
// The day's free times: everyone's, or one stylist's (their own times, and
// `who` so the post can say whose). A self-employed stylist's week is only
// theirs, so theirs is the day.
export const freeDayFrom = (week, date, staffId = null) => {
  if (!week || !week.length) return null
  const id = staffId || (week[0].who && week[0].who.id) || null
  if (id) {
    const theirs = week.map((d) => {
      const s = (d.byStaff || []).find(x => x.id === String(id))
      return s ? { date: d.date, dayLabel: d.dayLabel, times: s.times, all: s.all, count: s.count, staff: [s.firstName], who: { id: s.id, firstName: s.firstName }, mine: s.id === myStaffId() } : null
    }).filter(Boolean)
    if (date) return theirs.find(d => d.date === date) || null
    return theirs[0] || null
  }
  if (date) return week.find(d => d.date === date) || null
  return week[0]
}

// Whose free times a request is about: "Jess's free times", "free slots
// with Jess", "Jess has a gap": one of the stylists free this week, or null
const escape = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
export const stylistNamed = (text, week) => {
  const people = []
  ;(week || []).forEach(d => (d.byStaff || []).forEach((p) => {
    if (p.firstName && p.firstName.length > 1 && !people.some(x => x.id === p.id)) people.push(p)
  }))
  const t = String(text || '').replace(/[‘’]/g, "'")
  return people.find((p) => {
    const n = escape(p.firstName)
    return new RegExp(`\\b${n}(?:'s|s')?\\s+(?:free|gaps?|times?|slots?|openings?|spaces?|availability|diary|chair|is\\s+free|has)\\b|\\b(?:with|for|from)\\s+${n}\\b`, 'i').test(t)
  }) || null
}

// A free times post made from one stylist's diary is theirs, so their name
// goes on it: only when its day and times are that day's free ones
export const withStylist = (post, ctx = {}) => {
  const day = ctx.freeDay
  if (!post || !day || !day.who || (post.type !== 'freeSlots' && post.type !== 'oneSlot')) return post
  const c = post.content || {}
  if (c.date !== day.date || c.staffId === day.who.id) return post
  const times = (post.type === 'oneSlot' ? [c.time] : (c.times || [])).filter(Boolean)
  const free = (day.all && day.all.length ? day.all : day.times || []).map(minutesOf)
  if (!times.length || !times.every(t => free.includes(minutesOf(t)))) return post
  return { ...post, content: { ...c, staffId: day.who.id, staffName: day.who.firstName } }
}

// Everything a request needs, as ctx for applyRequest / createFromRequest.
// `have` passes records the page already holds (its team, say).
export const gatherAsk = async (req, post, { dispatch = null, have = {}, now = new Date(), wait = 5000 } = {}) => {
  const want = dataFor(req, post)
  const ctx = {}
  const jobs = []
  if (want.reviews) jobs.push(once('reviews', () => loadReviews({ limit: 50 }).then(r => (r && r.reviews) || null)).then((v) => { ctx.reviews = v }))
  if (want.team) {
    if (have.team) ctx.team = have.team
    else if (dispatch) jobs.push(once('team', () => loadTeam(dispatch)).then((v) => { ctx.team = v }))
  }
  if (want.services && dispatch) jobs.push(once('services', () => loadServices(dispatch)).then((v) => { ctx.services = v }))
  if (want.vouchers) jobs.push(once('vouchers', loadVouchers).then((v) => { ctx.vouchers = v }))
  if (want.free) {
    jobs.push(once(`free:${now.toDateString()}:${now.getHours()}`, () => freeWeek({ days: 8, now }), 2 * MINUTES).then((week) => {
      ctx.diaryRead = !!week
      // "Jess's free times": hers; else the post's own stylist's, if it has one
      const named = stylistNamed(req && req.text, week)
      const whose = named ? named.id : (post && post.content && post.content.staffId) || null
      ctx.freeDay = freeDayFrom(week, req && req.fields && req.fields.date, whose)
      if (named && !ctx.freeDay) ctx.freeNamed = named.firstName
    }))
  }
  await within(Promise.all(jobs), wait)
  return ctx
}

// gatherAsk, with the app's store (for the menu and team), from any screen
export const useGatherAsk = (have) => {
  const redux = useContext(ReactReduxContext)
  const dispatch = redux && redux.store ? redux.store.dispatch : null
  return useMemo(() => (req, post, opts = {}) => gatherAsk(req, post, { dispatch, have: have || {}, ...opts }), [dispatch, have])
}
